/**
 * FloorHspBadge — V14.77.
 *
 * Badge « HSP x.xx m » dans le header de l'Édition Plan, toujours visible
 * (même sidebar repliée). Clic → édition inline de la Hauteur Sous Plafond
 * de l'étage du lot sélectionné (PATCH /floors/{id}).
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FloorHspBadge = ({ lot, onPatched }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const floorId = lot?.floor_id;

  const save = useCallback(async () => {
    const val = parseFloat(String(draft).replace(',', '.'));
    if (!Number.isFinite(val) || val < 1.8 || val > 4.0) {
      toast.error('HSP invalide (entre 1.80 et 4.00 m).');
      return;
    }
    try {
      const { data } = await axios.patch(
        `${API}/floors/${floorId}`, { hsp_m: val }, { withCredentials: true });
      toast.success(`HSP → ${data.hsp_m.toFixed(2)} m (${data.name || 'étage'})`);
      setEditing(false);
      if (onPatched) onPatched(data);
    } catch {
      toast.error('Impossible de sauvegarder la HSP.');
    }
  }, [draft, floorId, onPatched]);

  if (!floorId) return null;
  const hsp = Number.isFinite(lot?.floor_hsp_m) ? lot.floor_hsp_m : 2.50;

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 h-7 px-1.5 rounded border border-amber-400/40 bg-amber-500/10"
            data-testid="floor-hsp-editor">
        <span className="text-[9.5px] uppercase tracking-wider text-amber-300/80">HSP</span>
        <input
          type="number" min={1.8} max={4} step={0.05} autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setEditing(false);
          }}
          className="w-14 h-5 text-[11px] px-1 rounded bg-black/30 border border-white/10 text-amber-100 text-right focus:outline-none"
          data-testid="floor-hsp-input"
        />
        <span className="text-[10px] text-amber-300/70">m</span>
        <button type="button" onClick={save}
                className="text-[10px] font-bold text-emerald-300 hover:text-emerald-200 px-0.5"
                data-testid="floor-hsp-save">✓</button>
        <button type="button" onClick={() => setEditing(false)}
                className="text-[10px] text-gray-400 hover:text-gray-200 px-0.5">✕</button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setDraft(hsp.toFixed(2)); setEditing(true); }}
      className="inline-flex items-center gap-1 text-[10.5px] h-7 px-2 rounded border border-white/10 text-amber-200/90 hover:border-amber-400/40 hover:bg-amber-500/10 font-mono"
      title={`Hauteur Sous Plafond de l'étage${lot?.floor_name ? ` « ${lot.floor_name} »` : ''} — clic pour éditer (utilisée par la 3D et le prompt IA)`}
      data-testid="floor-hsp-badge"
    >
      <ArrowUpDown className="w-3 h-3" />
      HSP {hsp.toFixed(2)}m
    </button>
  );
};
